import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import FancyTreeViewModelT from '@/ViewModels/Controls/FancyTreeViewModel';
import CheckBoxViewModelT from '@/ViewModels/Controls/CheckBoxViewModel';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import {ExtendedNodeData, ICoreFrameDropDownTreeViewConstructorParameter} from '#/DefaultViewExtensionInterfaces';
import TreeUtils from '@/Utility/TreeUtilities';
import * as StringFormatter from '@/Utility/StringFormatter';
import Utilities from '@/Utility/Utilities';

export default class CoreFrameDropDownTreeViewViewModel extends ControlViewModel {
    private FancyTreeViewModel = ComponentManager.getViewModel<typeof FancyTreeViewModelT>('FancyTreeViewModel');
    private CheckBoxViewModel = ComponentManager.getViewModel<typeof CheckBoxViewModelT>('CheckBoxViewModel');
    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');

    public isExpanded: KnockoutObservable<boolean>;
    public tree: FancyTreeViewModelT;
    public selection: KnockoutObservable<string>;
    public errorLabel: LabelViewModelT;
    public selectionTooltipLabel: LabelViewModelT;
    public activateNodeByKey: KnockoutObservable<string>;
    public removeNodeByKeyObservable: KnockoutObservable<string>;
    public fullValues: KnockoutObservableArray<any>;
    public lazyLoading: boolean;
    public selectionTooltip: KnockoutObservable<string>;
    public isMultiselect: boolean;
    
    public onOpenCallback: () => void;
    public onCloseCallback: () => void;

    private readonly onChecked: (value: string | boolean) => void;
    private readonly onUnchecked: (value: string | boolean) => void;
    private readonly isSingleSelect: boolean;
    private readonly hasCheckboxes: boolean;
    private readonly dataSource: KnockoutObservableArray<ExtendedNodeData>;
    private readonly keysOfCheckedNodes: KnockoutObservableArray<string>;
    
    private nodesByKey: { [key: string]: ExtendedNodeData } = {};
    private artificialNodes: Array<ExtendedNodeData>;

    public constructor(protected params: ICoreFrameDropDownTreeViewConstructorParameter) {
        super(params);

        this.errorLabel = params.errorLabel;
        
        this.isExpanded = this.observableOrDefault(false);
        this.defineSubscription(this.isExpanded, (isExpanded: boolean) => {
            if (isExpanded)
                this.onOpenCallback();
            else
                this.onCloseCallback();
        });

        this.activateNodeByKey = this.observableOrDefault("");
        this.removeNodeByKeyObservable = this.observableOrDefault("");

        this.hasCheckboxes = this.paramOrDefault(params.hasCheckboxes, false);
        this.isMultiselect = this.paramOrDefault(params.isMultiselect, false);
        this.isSingleSelect = !this.isMultiselect;
        this.fullValues = this.observableOrDefault(params.fullValues, []) as KnockoutObservableArray<any>;
        this.lazyLoading = this.paramOrDefault(params.lazyLoading, false);
        
        this.onOpenCallback = this.handlerOrDefault(params.onOpenCallback, () => {});
        this.onCloseCallback = this.handlerOrDefault(params.onClosedCallback, () => {});
        
        this.onChecked = this.handlerOrDefault(params.onChecked, () => {});
        this.onUnchecked = this.handlerOrDefault(params.onUnchecked, () => {});

        // selection means all selected options joined to a string shown in the area-div of the control
        this.selection = this.observableOrDefault("");
        this.selectionTooltip = this.observableOrDefault("");
        this.selectionTooltipLabel = new this.LabelViewModel({ caption: this.selectionTooltip });

        this.dataSource = this.observableOrDefault(this.params.dataSource, []) as KnockoutObservableArray<ExtendedNodeData>;
        this.keysOfCheckedNodes = this.sanitizeSelectedKeysParameter(this.params.selectedKeys);

        this.initializeTree();

        // render tree as soon as the data-request returns
        this.defineSubscription(this.dataSource, (dataSource) => {
            let processedDataSource = this.processDataSource(dataSource);
            if (_.isEmpty(processedDataSource)) {
                return;
            }

            this.addCheckBoxViewModels();
            this.subscribeToIsCheckedObservables();
            this.syncViewSelection(this.keysOfCheckedNodes());

            this.tree.nodes(processedDataSource);
        })(this.dataSource());
        
        this.defineSubscription(this.fullValues, (fullValues) => {
           if (!this.lazyLoading)
               return;
           
           this.displaySelectedValuesOnLazyLoad(fullValues);
        });

        this.defineSubscription(this.keysOfCheckedNodes, keys => this.syncViewSelection(keys));
    }

    public removeNodeByKey(key: string) {
        let source = this.dataSource();

        // remove from datasource
        let couldRemove = TreeUtils.removeNodeByKey(source, key) != null;
        if (!couldRemove) {
            return;
        }

        // remove from fancytree
        this.removeNodeByKeyObservable(key);

        // remove from keysOfCheckedNodes
        this.keysOfCheckedNodes(this.keysOfCheckedNodes().filter(anyKey => anyKey != key));
    }

    public getNodeByKey(key: string): ExtendedNodeData {
        return this.nodesByKey[key];
    }

    private processDataSource(dataSource: Array<ExtendedNodeData>): Array<ExtendedNodeData> {
        let allNodes = this.getAllNodes(dataSource);
        if (!this.validateDataSourceKeyUniqueness(allNodes)) {
            const invalidMsg: string = 'Invalid data-source, non-unique keys detected!'
            this.tree.nodes([]);
            this.selection(invalidMsg);
            this.selectionTooltip(invalidMsg);
            return [];
        }

        allNodes.forEach(node => this.nodesByKey[node.key] = node);

        this.artificialNodes = this.createArtificialNodes();
        this.artificialNodes.forEach(node => this.nodesByKey[node.key] = node);

        return _.union(dataSource, this.artificialNodes);
    }

    private sanitizeSelectedKeysParameter(selectedKeysParam: KnockoutObservableArray<string>): KnockoutObservableArray<string> {
        const keysObservable = this.observableOrDefault(selectedKeysParam, []) as KnockoutObservableArray<string>;
        if (keysObservable().length > 1 && this.isSingleSelect) {
            console.log('More than one selected nodes are given in single-select-mode!');
            keysObservable([keysObservable.shift()]);
        }
        
        return keysObservable;
    }
    
    private displaySelectedValuesOnLazyLoad(selected: Array<Array<string>>) {
        const flatten = selected
            // [['PDO_ID', 'Caption']]
            .map(selectedAssociation => selectedAssociation[1])
            .filter(caption => caption !== undefined);

        this.syncDisplayedValue(flatten);
    }
    
    private syncViewSelection(keys) {
        let nodes = keys
            .map(key => this.nodesByKey[key])
            .filter(node => node !== undefined && node.title !== undefined) as Array<ExtendedNodeData>;

        nodes.forEach(node => node.checkBoxViewModel.isChecked(true));

        let titlesOfCheckedNodes = nodes.map(node => node.title);

        this.syncDisplayedValue(titlesOfCheckedNodes);
    }
    
    private syncDisplayedValue(selected: Array<string>) {
        let selection = selected.join('; ');
        let tooltip = selected.join('\r\n');
        const caption = this.tooltip?.caption();

        if (!Utilities.isNullOrUndefinedOrBlank(caption)) {
            if (!_.isEmpty(selected))
                tooltip = caption + '\r\n\r\n' + tooltip;
            else
                tooltip = caption;
        }

        this.selection(selection);
        this.selectionTooltip(tooltip);
    }

    private addCheckBoxViewModels() {
        let keysOfDataSourceNodes = Object.keys(this.nodesByKey);

        keysOfDataSourceNodes
            .map(key => this.nodesByKey[key])
            .forEach(node => this.addCheckBoxViewModel(node));
    }

    private addCheckBoxViewModel(node: ExtendedNodeData): void {
        node.checkBoxViewModel = new this.CheckBoxViewModel({
            value: node.key,
            isVisible: this.defineComputed(() => {
                let isCheckable = node.isCheckable ? node.isCheckable() !== false : true;
                return this.hasCheckboxes && isCheckable;
            }),
            isEnabled: this.defineComputed(() => {
                let isCheckable = node.isCheckable ? node.isCheckable() !== false : true;
                return this.isEnabled() && isCheckable;
            }),
            isChecked: this.observableOrDefault(_.contains(this.params.selectedKeys(), node.key)),
            locator: node["locator"]
        });

        if (!node.data) {
            node.data = {};
        }
        node.data["isExpandedObs"] = node["isExpandedObs"];
    }

    // adds subscriptions to each checkbox'-isChecked-observable
    private subscribeToIsCheckedObservables(): void {
        Object.keys(this.nodesByKey).forEach(key => {
            let node = this.nodesByKey[key];
            let subscribingCheckBox = node.checkBoxViewModel;
            let subscribingObservable = subscribingCheckBox.isChecked;

            // keep this.keysOfCheckedNodes in sync
            this.defineSubscription(subscribingObservable, (isChecked => {
                let isNotCheckedAnymore = !isChecked;

                if (isNotCheckedAnymore) {
                    this.keysOfCheckedNodes(this.keysOfCheckedNodes().filter(anyKey => anyKey !== key));
                }
                else if (!_.contains(this.keysOfCheckedNodes(), key)) {
                    this.keysOfCheckedNodes.push(key);
                }
            }));

            // maintain single-select-condition
            if (this.isSingleSelect) {
                this.defineSubscription(subscribingObservable, (isChecked => {
                    if (isChecked) {
                        this.uncheckOtherCheckboxes(subscribingCheckBox);
                    }
                }))(subscribingObservable());
            }

            // close the popup immediately after selection-change in single-select-mode
            if (this.isSingleSelect) {
                this.defineSubscription(subscribingObservable, (isChecked => {
                    if (isChecked) {
                        this.async(() => {
                            this.isExpanded(false);
                            // allow fancytree to perform nodeSetFocus & nodeSetActive before closing the popup
                            // otherwise a warning is thrown to console
                        }, 5);
                    }
                }));
            }
            
            this.defineSubscription(subscribingObservable, (isChecked => {
                if (isChecked) {
                    if (this.onChecked)
                        this.onChecked(subscribingCheckBox.value());
                } else {
                    if (this.onUnchecked)
                        this.onUnchecked(subscribingCheckBox.value())
                }
            }));
            
            this.defineSubscription(subscribingObservable, (isChecked => {
                if (!isChecked && node.isArtificial) {
                    this.async(() => {
                        this.removeNodeByKeyObservable(node.key);

                        this.artificialNodes = this.artificialNodes.filter(anyNode => anyNode.key !== node.key) // array
                        delete this.nodesByKey[node.key]; // object
                        node.checkBoxViewModel.dispose();
                    }, 30); // waits for fancytree to finish setting focus to the node that we want to delete
                }
            }));
        });
    }

    private uncheckOtherCheckboxes(checkBox: CheckBoxViewModelT): void {
        Object.keys(this.nodesByKey)
            .map(key => this.nodesByKey[key])
            .map(node => node.checkBoxViewModel)
            .filter(anyCheckBox => anyCheckBox !== checkBox)
            .forEach(anyCheckBox => anyCheckBox.isChecked(false));
    }

    private validateDataSourceKeyUniqueness(allNodes: Array<ExtendedNodeData>): boolean {
        let duplicateKeys = allNodes
            .map(node => node.key)
            .filter((value, index, array) => array.indexOf(value) !== index);

        if (!_.isEmpty(duplicateKeys)) {
            console.log("The Keys", duplicateKeys, "are not unique.");
        }

        return _.isEmpty(duplicateKeys);
    }

    private getAllNodes(nodes: Array<ExtendedNodeData>): Array<ExtendedNodeData> {
        if (_.isEmpty(nodes)) {
            return [];
        }

        return _.flatten([]
            .concat(nodes)
            .concat(nodes.map(node => node == null ? null : this.getAllNodes(node.children))))
            .filter(x => x != null);
    }

    // there may be keys of selected nodes that are not
    // included in the datasource
    private createArtificialNodes() {
        return this.keysOfCheckedNodes()
            .filter(key => !_.contains(Object.keys(this.nodesByKey), key))
            .map(key => {
                let fullValues = ko.utils.unwrapObservable(this.params["fullValues"]) || [];
                let matchInFullValues = fullValues.filter(entry => entry[0] == key).pop();
                let title = matchInFullValues ? matchInFullValues[1] : "## missing title ##";

                return {
                    key: key,
                    title: title,
                    isChecked: this.observableOrDefault(true),
                    isArtificial: true
                } as ExtendedNodeData
            });
    }

    private initializeTree(): void {
        this.tree = new this.FancyTreeViewModel({
            nodes: this.observableOrDefault([]) as KnockoutObservableArray<ExtendedNodeData>,
            extensions: ["wide"],
            renderCheckboxes: false,
            renderIcons: false,
            autoScroll: true,
            isVisible: this.isExpanded
        });
    }
}

ComponentManager.setViewModel('CoreFrameDropDownTreeViewViewModel', CoreFrameDropDownTreeViewViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');